import React, { useCallback } from "react";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, InputNumber } from "formik-antd";
import { settingService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getInitialValues from "./formHelper";
import { handlePromiseError } from "/app/src/helpers/api";

interface FormValues {
  value: string | undefined;
}

/**
 * Form for creating and updating the request timeout setting
 * @param integration Integration
 */
export default function Timeout({ integration }: { integration: Integration }) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const timeoutSettingQuery = useQuery({
    queryKey: ["setting", "timeout", integration.id],
    queryFn: () =>
      settingService.getAll(
        buildParams({ integrationId: integration.id, type: "timeout" }),
      ),
  });

  const { mutateAsync: timeoutMutation } = useMutation({
    mutationFn: (values: FormValues) => {
      if (timeoutSettingQuery.data?.settings.length < 1) {
        return settingService
          .createSingle({
            name: "timeout",
            value: values.value,
            type: "timeout",
            integrationId: integration.id,
          })
          .then(handlePromiseError);
      } else {
        return settingService
          .updateSingle(timeoutSettingQuery.data?.settings[0]?.id, {
            value: values.value,
          })
          .then(handlePromiseError);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["setting", "timeout", integration.id], {
        settings: [data.setting],
      });
    },
  });

  const timeoutSubmitHandler = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await timeoutMutation(values).finally(() => {
        actions.resetForm();
      });
    },
    [timeoutMutation],
  );
  /**
   * Form for the request timeout setting
   */
  const timeoutForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting }) => (
        <Form layout="vertical">
          <Row justify="start" gutter={16}>
            <Col span={12}>
              <Form.Item
                name="value"
                label={t("translation:timeout")}
                hasFeedback={false}
              >
                <InputNumber
                  name="value"
                  min={1}
                  max={90}
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="10"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <SubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || isSubmitting}
                style={{ marginTop: "30px" }}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [t],
    );
  return (
    <Formik
      component={timeoutForm}
      initialValues={{
        value: getInitialValues(timeoutSettingQuery.data?.settings[0]?.value),
      }}
      enableReinitialize
      onSubmit={timeoutSubmitHandler}
    />
  );
}
